import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
  MapPinIcon,
  StarIcon,
  EyeIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline'
import { useActiveProviders } from '../../../hooks/useProviders'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { ServiceProvider, ServiceType } from '../../../types'

const serviceTypeLabels: Record<ServiceType, string> = {
  AMBULANCE_PROVIDER: 'Ambulance',
  USTADZ: 'Ustadz',
  CEMETERY_PROVIDER: 'Cemetery',
  VENDOR: 'Vendor',
  PROVIDER: 'General Provider',
}

const serviceTypeColors: Record<ServiceType, string> = {
  AMBULANCE_PROVIDER: 'bg-blue-100 text-blue-800',
  USTADZ: 'bg-purple-100 text-purple-800',
  CEMETERY_PROVIDER: 'bg-green-100 text-green-800',
  VENDOR: 'bg-orange-100 text-orange-800',
  PROVIDER: 'bg-gray-100 text-gray-800',
}

export default function ActiveProvidersPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [localSearchTerm, setLocalSearchTerm] = useState('')
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceType | ''>('')
  const [selectedCity, setSelectedCity] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | null>(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const debounceTimerRef = useRef<NodeJS.Timeout>()
  const pageSize = 10

  // Debounced search update
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchTerm(localSearchTerm)
      setCurrentPage(1)
    }, 300) // 300ms debounce delay

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [localSearchTerm])

  const { data: activeProvidersData, isLoading, error, refetch } = useActiveProviders({
    page: currentPage,
    limit: pageSize,
    serviceType: selectedServiceType || undefined,
    city: selectedCity || undefined,
    search: searchTerm || undefined,
  })

  const providers = activeProvidersData?.data || []
  const meta = activeProvidersData?.meta

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleRefresh = () => {
    refetch()
  }

  const resetFilters = () => {
    setLocalSearchTerm('')
    setSearchTerm('')
    setSelectedServiceType('')
    setSelectedCity('')
    setCurrentPage(1)
  }

  const handleViewProvider = (provider: ServiceProvider) => {
    setSelectedProvider(provider)
    setShowViewModal(true)
  }

  const handleCopyProviderId = async (providerId: string) => {
    try {
      await navigator.clipboard.writeText(providerId)
      // You could add a toast notification here if you have one
      console.log('Provider ID copied to clipboard')
    } catch (err) {
      console.error('Failed to copy provider ID:', err)
    }
  }

  const handleCloseViewModal = () => {
    setShowViewModal(false)
    setSelectedProvider(null)
  }

  
  if (isLoading && !providers.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Active Providers</h1>
            <p className="text-gray-600 mt-1">Manage approved service providers</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <Link
              to="/admin/providers/pending"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              View Pending
            </Link>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{meta?.total || 0}</p>
              <p className="text-gray-600">Total Active Providers</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <StarIcon className="h-8 w-8 text-yellow-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">
                {providers.length > 0
                  ? (providers.reduce((acc, p) => acc + (p.rating || 0), 0) / providers.length).toFixed(1)
                  : '0.0'
                }
              </p>
              <p className="text-gray-600">Average Rating</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <MapPinIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">
                {new Set(providers.map(p => p.city).filter(Boolean)).size}
              </p>
              <p className="text-gray-600">Cities Served</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Search providers..."
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
            {(selectedServiceType || selectedCity) && (
              <span className="ml-2 bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
                Active
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Type
                </label>
                <select
                  value={selectedServiceType}
                  onChange={(e) => {
                    setSelectedServiceType(e.target.value as ServiceType | '')
                    setCurrentPage(1)
                  }}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="">All Types</option>
                  {Object.entries(serviceTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={selectedCity}
                  onChange={(e) => {
                    setSelectedCity(e.target.value)
                    setCurrentPage(1)
                  }}
                  placeholder="Enter city..."
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={resetFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="text-center py-12">
          <div className="text-red-600 text-lg font-medium">Failed to load active providers</div>
          <p className="text-gray-500 mt-2">Please try again later</p>
        </div>
      )}

      {/* Providers List */}
      {!error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {providers.length === 0 && !isLoading ? (
            <div className="text-center py-12">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No active providers found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {localSearchTerm || selectedServiceType || selectedCity
                  ? 'No providers match your search criteria'
                  : 'No providers have been approved yet'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-200">
                {providers.map((provider) => (
                  <div key={provider.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-medium text-gray-900 truncate">
                            {provider.companyName}
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${serviceTypeColors[provider.serviceType]}`}>
                            {serviceTypeLabels[provider.serviceType]}
                          </span>
                          <button
                            onClick={() => handleCopyProviderId(provider.id)}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors cursor-pointer"
                            title="Copy Provider ID"
                          >
                            <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                            ID: {provider.id.substring(0, 8)}...
                          </button>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        </div>

                        <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                          <div className="flex items-center">
                            <UserGroupIcon className="h-4 w-4 mr-1" />
                            {provider.user.firstName} {provider.user.lastName}
                          </div>
                          <div className="flex items-center">
                            <MapPinIcon className="h-4 w-4 mr-1" />
                            {provider.city || 'Not specified'}
                          </div>
                          {provider.rating && (
                            <div className="flex items-center">
                              <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                              {provider.rating.toFixed(1)}
                            </div>
                          )}
                        </div>

                        <div className="mt-2 text-sm text-gray-600">
                          <p className="font-medium">Contact: {provider.user.email}</p>
                          {provider.user.phone && <p>Phone: {provider.user.phone}</p>}
                          {provider.description && (
                            <p className="mt-1">{provider.description.substring(0, 150)}...</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <Link
                          to={`/admin/providers/active/${provider.id}`}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          View
                        </Link>
                        <button
                          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <PencilSquareIcon className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {meta && meta.totalPages > 1 && (
                <div className="border-t border-gray-200 px-6 py-3 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((meta.page - 1) * meta.limit) + 1} to{' '}
                    {Math.min(meta.page * meta.limit, meta.total)} of{' '}
                    {meta.total} results
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(meta.page - 1)}
                      disabled={!meta.hasPrev}
                      className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>

                    <span className="text-sm text-gray-700">
                      Page {meta.page} of {meta.totalPages}
                    </span>

                    <button
                      onClick={() => handlePageChange(meta.page + 1)}
                      disabled={!meta.hasNext}
                      className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Provider Details Modal */}
      {showViewModal && selectedProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Provider Details</h2>
                <button
                  onClick={handleCloseViewModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Business Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Business Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Business Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedProvider.companyName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Provider ID</label>
                    <div className="mt-1 flex items-center space-x-2">
                      <p className="text-sm text-gray-900 font-mono">{selectedProvider.id}</p>
                      <button
                        onClick={() => handleCopyProviderId(selectedProvider.id)}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Copy Provider ID"
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Service Type</label>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${serviceTypeColors[selectedProvider.serviceType]}`}>
                        {serviceTypeLabels[selectedProvider.serviceType]}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">City</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedProvider.city || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Rating</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedProvider.rating ? (
                        <div className="flex items-center">
                          <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                          {selectedProvider.rating.toFixed(1)}
                        </div>
                      ) : 'Not rated yet'}
                    </p>
                  </div>
                </div>
                {selectedProvider.description && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-500">Description</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedProvider.description}</p>
                  </div>
                )}
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Contact Name</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedProvider.user.firstName} {selectedProvider.user.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedProvider.user.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Phone</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedProvider.user.phone || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Status</label>
                    <p className="mt-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedProvider.responseRate || 0}%
                    </div>
                    <div className="text-sm text-gray-500">Response Rate</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedProvider.averageResponseTime || 0}m
                    </div>
                    <div className="text-sm text-gray-500">Avg Response Time</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedProvider.totalBookings || 0}
                    </div>
                    <div className="text-sm text-gray-500">Total Bookings</div>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Important Dates</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Registration Date</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(selectedProvider.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Last Active</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedProvider.lastActive
                        ? new Date(selectedProvider.lastActive).toLocaleDateString()
                        : 'Unknown'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCloseViewModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}